import React, { useState, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Controller } from 'react-hook-form';

import Modal from '@/components/atoms/Modal';
import Select from '@/components/atoms/Select';
import Loader from '@/components/atoms/Loader';
import { SvyButton } from '@/components/savory/SvyButton';
import { Input } from '@/components/atoms/Form/Input';
import { Table } from '@/components/atoms/Table';

import { GrantStrategy, GrantType } from '@/services/innovorder/grant/grant.type';
import { computeCurrency, computeText, computePercentage } from '@/locales/utils';
import { SubmitButton } from '@/core/form/SubmitButton';
import { Form } from '@/core/form/Form';

import { HeaderContainer } from './GrantShifts.style';
import { useGrantShiftsVM } from './useGrantShiftsVm';
import { GrantStrategyMap } from '../GrantForm';

export type GrantShiftsProps = {
    restaurantId: number;
    name: string;
    grantType: GrantType;
    isCreateMode: boolean;
    handleOnHide: () => void;
};

const GrantShifts: React.FunctionComponent<React.PropsWithChildren<GrantShiftsProps>> = (props) => {
    const { name, handleOnHide, grantType } = props;

    const intl = useIntl();
    const {
        controlRestaurantGrant,
        columns,
        rows,
        isLoading,
        dailyLimitMax,
        handleOnRowClick,
        handleOnSubmit,
        isWarningModalOpen,
        handleCloseWarning,
        handleConfirmWarning,
        dangerousItems,
    } = useGrantShiftsVM(props);

    const [canConfirm, setCanConfirm] = useState(false);

    useEffect(() => {
        let timer: ReturnType<typeof setTimeout> | undefined;

        if (isWarningModalOpen) {
            setCanConfirm(false);
            timer = setTimeout(() => {
                setCanConfirm(true);
            }, 2500);
        }

        return () => {
            if (timer) clearTimeout(timer);
        };
    }, [isWarningModalOpen]);

    const isPercentage = grantType === 'RATE';

    return (
        <Form>
            <Modal
                isOpen={isWarningModalOpen}
                onHide={handleCloseWarning}
                title={computeText(intl, 'grant.warningShifts.title')}
                buttons={[
                    <SvyButton
                        key="confirm-warning"
                        onClick={handleConfirmWarning}
                        disabled={!canConfirm}
                        color="primary"
                        variant="default"
                    >
                        {computeText(intl, 'button.confirm')}
                    </SvyButton>,
                ]}
            >
                <div>
                    <span>
                        {dangerousItems
                            .map((item) =>
                                computeText(intl, 'grant.warningShifts.shift', {
                                    time: item.shift.name,
                                    amount: isPercentage
                                        ? computePercentage({ amount: item.amount / 100 })
                                        : computeCurrency({ intl, amount: item.amount }),
                                    variation: isPercentage
                                        ? computePercentage({ amount: item.diff / 100 })
                                        : computeCurrency({ intl, amount: item.diff }),
                                }),
                            )
                            .join('\n')}
                    </span>
                    <br />
                    <span>{computeText(intl, 'grant.warningShifts.confirm')}</span>
                </div>
            </Modal>
            <Modal
                data-testid="grant-shifts-form"
                isOpen={true}
                onHide={handleOnHide}
                title={computeText(intl, 'grant.configuration.title', { name })}
                buttons={[
                    <SubmitButton key="grant-shifts-submit-btn" onClick={handleOnSubmit}>
                        {!isLoading && <FormattedMessage id="button.save" />}
                        {isLoading && <Loader />}
                    </SubmitButton>,
                ]}
            >
                <HeaderContainer>
                    <Controller
                        control={controlRestaurantGrant}
                        name="strategy"
                        rules={{ required: computeText(intl, 'field.required') }}
                        render={({ field: { onChange, value } }) => (
                            <Select<GrantStrategy>
                                value={value}
                                options={GrantStrategyMap}
                                onChange={onChange}
                                labelId="grant.strategy"
                                isTouched={false}
                            />
                        )}
                    />
                    <Controller
                        control={controlRestaurantGrant}
                        name="dailyLimit"
                        rules={{ required: computeText(intl, 'field.required') }}
                        render={({ field: { onChange, value }, fieldState: { error } }) => {
                            return (
                                <Input
                                    error={error && computeText(intl, 'grant.warningLimit')}
                                    onChange={onChange}
                                    value={value?.toString()}
                                    placeholder="grant.dailyLimit"
                                    type="number"
                                    labelId="grant.dailyLimit"
                                    min={0}
                                    max={dailyLimitMax}
                                />
                            );
                        }}
                    />
                </HeaderContainer>

                <Table
                    rows={rows}
                    columns={columns}
                    emptyMessageId="grant.emptyMessage"
                    isSearchable={true}
                    searchPlaceholder="grant.configuration.search"
                    filters={['name']}
                    hasPagination={true}
                    onRowClick={handleOnRowClick}
                />
            </Modal>
        </Form>
    );
};

export default GrantShifts;
